/**
 * Render a patient-shared CMR snapshot as a compact text block for LLM prompts.
 * Returns "" if nothing meaningful was shared, so callers can concat blindly.
 */
export function formatPatientContext(ctx) {
  if (!ctx || !ctx.record) return '';
  const lines = [];
  const { record, patientName, patientDob, allowedSections = [] } = ctx;

  const header = [];
  if (patientName) header.push(patientName);
  if (patientDob) header.push(`DOB ${patientDob}`);
  if (header.length) lines.push(`Patient: ${header.join(' • ')}`);

  if (record.demographics) {
    const { sex, weight } = record.demographics;
    const bits = [sex, weight ? `${weight} lb` : null].filter(Boolean);
    if (bits.length) lines.push(`Demographics: ${bits.join(', ')}`);
  }

  const renderList = (label, items, pick) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const rendered = items.map(pick).filter(Boolean);
    if (rendered.length) lines.push(`${label}: ${rendered.join('; ')}`);
  };

  renderList('Medications', record.medications, (m) =>
    [m.name || m.medicationName, m.dosage, m.frequency].filter(Boolean).join(' '),
  );
  renderList('Allergies', record.allergies, (a) =>
    typeof a === 'string' ? a : [a.allergen || a.name, a.reaction].filter(Boolean).join(' — '),
  );
  renderList('Past medical history', record.conditions, (c) =>
    typeof c === 'string' ? c : c.condition || c.name || c.diagnosis,
  );
  renderList('Past surgical history', record.surgical, (s) =>
    typeof s === 'string' ? s : [s.procedure || s.name, s.year].filter(Boolean).join(' '),
  );

  if (record.other && typeof record.other === 'object') {
    const entries = Object.entries(record.other)
      .filter(([, v]) => v && String(v).trim() !== '')
      .map(([k, v]) => `${k}: ${v}`);
    if (entries.length) lines.push(`Other history — ${entries.join('; ')}`);
  }

  if (lines.length === 0) return '';

  return [
    `PATIENT CONTEXT (patient-shared, sections: ${allowedSections.join(', ') || '—'}; may be partial):`,
    ...lines,
    '',
  ].join('\n');
}

export default formatPatientContext;
